// Games & Randomizers helpers: bingo, lottery, dice, passwords, Sudoku,
// Yu-Gi-Oh life points, fuel economy, IP detection, coin flips, Magic 8-Ball,
// card draws, and team splitting. All randomness runs client-side (this
// engine is invoked in the browser), so plain Math.random() is fine — these
// are fun/utility generators, not security-sensitive.
import { t } from './i18n'

// "Bingo Number Generator" - draws unique numbers 1-75, tagged with the
// standard US Bingo column letter (B 1-15, I 16-30, N 31-45, G 46-60, O 61-75)
export function bingoNumberGenerator(params: { num_balls: unknown }): { drawn_numbers: string } {
    const numBalls = Math.min(Math.max(Math.round(Number(params.num_balls)) || 1, 1), 75)
    const pool = Array.from({ length: 75 }, (_, i) => i + 1)
    const drawn: number[] = []
    for (let i = 0; i < numBalls; i++) {
        const idx = Math.floor(Math.random() * pool.length)
        drawn.push(pool.splice(idx, 1)[0])
    }
    const letterFor = (n: number) => (n <= 15 ? 'B' : n <= 30 ? 'I' : n <= 45 ? 'N' : n <= 60 ? 'G' : 'O')
    return { drawn_numbers: drawn.map((n) => `${letterFor(n)}-${n}`).join(', ') }
}

// "Lottery Number Picker" - configurable main-number pool/count plus an
// optional single bonus number (e.g. Powerball-style), so it can model most
// national lottery formats
const NO_BONUS: Record<string, string> = {
    en: 'None (bonus disabled)', ru: 'Нет (бонус отключён)', de: 'Keine (Bonus deaktiviert)', es: 'Ninguno (bono desactivado)', fr: 'Aucun (bonus désactivé)', it: 'Nessuno (bonus disattivato)', pl: 'Brak (bonus wyłączony)', lv: 'Nav (bonuss atspējots)',
}
export function lotteryNumberPicker(params: { main_count: unknown; main_max: unknown; bonus_max: unknown; language: unknown }): {
    main_numbers: string; bonus_number: string
} {
    const mainCount = Math.min(Math.max(Math.round(Number(params.main_count)) || 1, 1), 20)
    const mainMax = Math.max(Math.round(Number(params.main_max)) || 1, mainCount)
    const bonusMax = Math.max(Math.round(Number(params.bonus_max)) || 0, 0)
    const pool = Array.from({ length: mainMax }, (_, i) => i + 1)
    const main: number[] = []
    for (let i = 0; i < mainCount; i++) {
        const idx = Math.floor(Math.random() * pool.length)
        main.push(pool.splice(idx, 1)[0])
    }
    main.sort((a, b) => a - b)
    const bonusNumber = bonusMax > 0 ? String(Math.floor(Math.random() * bonusMax) + 1) : t(NO_BONUS, params.language)
    return { main_numbers: main.join(', '), bonus_number: bonusNumber }
}

// "Dice Roller" - N dice of S sides, plus a flat modifier added to the total
export function diceRoller(params: { num_dice: unknown; sides: unknown; modifier: unknown }): {
    individual_rolls: string; total: number
} {
    const numDice = Math.min(Math.max(Math.round(Number(params.num_dice)) || 1, 1), 100)
    const sides = Math.max(Math.round(Number(params.sides)) || 6, 2)
    const modifier = Number(params.modifier) || 0
    const rolls: number[] = []
    for (let i = 0; i < numDice; i++) rolls.push(Math.floor(Math.random() * sides) + 1)
    const total = rolls.reduce((a, b) => a + b, 0) + modifier
    return { individual_rolls: rolls.join(', '), total }
}

// "Random Password Generator" - character pool grows with each enabled
// class; always includes lowercase as the base pool
export function randomPasswordGenerator(params: {
    length: unknown; include_uppercase: unknown; include_numbers: unknown; include_symbols: unknown
}): { password: string } {
    const length = Math.min(Math.max(Math.round(Number(params.length)) || 12, 4), 128)
    let pool = 'abcdefghijklmnopqrstuvwxyz'
    if (String(params.include_uppercase) === 'yes') pool += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    if (String(params.include_numbers) === 'yes') pool += '0123456789'
    if (String(params.include_symbols) === 'yes') pool += '!@#$%^&*()_+-='
    let out = ''
    for (let i = 0; i < length; i++) out += pool[Math.floor(Math.random() * pool.length)]
    return { password: out }
}

// "Sudoku Puzzle Generator" - fills a complete valid grid via randomized
// backtracking, then removes cells for the chosen difficulty. Cell removal
// doesn't verify the result still has a UNIQUE solution (that check is
// expensive) — acceptable for a casual puzzle generator, not a competition
// setter.
function makeEmptyGrid(): number[][] {
    return Array.from({ length: 9 }, () => Array(9).fill(0))
}
function isValidPlacement(grid: number[][], row: number, col: number, num: number): boolean {
    for (let i = 0; i < 9; i++) {
        if (grid[row][i] === num) return false
        if (grid[i][col] === num) return false
    }
    const startRow = row - (row % 3)
    const startCol = col - (col % 3)
    for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
            if (grid[startRow + i][startCol + j] === num) return false
        }
    }
    return true
}
function fillGrid(grid: number[][]): boolean {
    for (let row = 0; row < 9; row++) {
        for (let col = 0; col < 9; col++) {
            if (grid[row][col] === 0) {
                const nums = [1, 2, 3, 4, 5, 6, 7, 8, 9]
                for (let i = nums.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1))
                    ;[nums[i], nums[j]] = [nums[j], nums[i]]
                }
                for (const num of nums) {
                    if (isValidPlacement(grid, row, col, num)) {
                        grid[row][col] = num
                        if (fillGrid(grid)) return true
                        grid[row][col] = 0
                    }
                }
                return false
            }
        }
    }
    return true
}
function formatGrid(grid: number[][]): string {
    return grid.map((r) => r.map((c) => (c === 0 ? '.' : c)).join(' ')).join('\n')
}
export function sudokuPuzzleGenerator(params: { difficulty: unknown }): { puzzle: string; solution: string } {
    const grid = makeEmptyGrid()
    fillGrid(grid)
    const solution = grid.map((r) => r.slice())
    const difficulty = String(params.difficulty)
    const removeCount = difficulty === 'easy' ? 35 : difficulty === 'hard' ? 52 : 45
    const puzzle = grid.map((r) => r.slice())
    let removed = 0
    let attempts = 0
    while (removed < removeCount && attempts < 500) {
        const row = Math.floor(Math.random() * 9)
        const col = Math.floor(Math.random() * 9)
        if (puzzle[row][col] !== 0) {
            puzzle[row][col] = 0
            removed++
        }
        attempts++
    }
    return { puzzle: formatGrid(puzzle), solution: formatGrid(solution) }
}

// "Yu-Gi-Oh Life Point Calculator" - applies a change (positive or negative)
// to each player's life points and reports whether either has been defeated
// (life points at or below zero)
const GAME_CONTINUES: Record<string, string> = {
    en: 'Game continues — both players still standing.', ru: 'Игра продолжается — оба игрока ещё в игре.', de: 'Das Spiel geht weiter — beide Spieler stehen noch.', es: 'El juego continúa — ambos jugadores siguen en pie.', fr: 'La partie continue — les deux joueurs sont encore debout.', it: 'La partita continua — entrambi i giocatori sono ancora in piedi.', pl: 'Gra trwa — obaj gracze wciąż stoją.', lv: 'Spēle turpinās — abi spēlētāji vēl ir spēlē.',
}
const PLAYER1_DEFEATED: Record<string, string> = {
    en: 'Player 1 has been defeated!', ru: 'Игрок 1 повержен!', de: 'Spieler 1 wurde besiegt!', es: '¡El Jugador 1 ha sido derrotado!', fr: 'Le Joueur 1 a été vaincu !', it: 'Il Giocatore 1 è stato sconfitto!', pl: 'Gracz 1 został pokonany!', lv: 'Spēlētājs 1 ir sakauts!',
}
const PLAYER2_DEFEATED: Record<string, string> = {
    en: 'Player 2 has been defeated!', ru: 'Игрок 2 повержен!', de: 'Spieler 2 wurde besiegt!', es: '¡El Jugador 2 ha sido derrotado!', fr: 'Le Joueur 2 a été vaincu !', it: 'Il Giocatore 2 è stato sconfitto!', pl: 'Gracz 2 został pokonany!', lv: 'Spēlētājs 2 ir sakauts!',
}
const BOTH_DEFEATED: Record<string, string> = {
    en: 'Both players have been defeated — it\'s a draw!', ru: 'Оба игрока повержены — ничья!', de: 'Beide Spieler wurden besiegt — unentschieden!', es: '¡Ambos jugadores han sido derrotados — empate!', fr: 'Les deux joueurs ont été vaincus — égalité !', it: 'Entrambi i giocatori sono stati sconfitti — pareggio!', pl: 'Obaj gracze zostali pokonani — remis!', lv: 'Abi spēlētāji ir sakauti — neizšķirts!',
}
export function yugiohLifePointCalculator(params: {
    p1_starting_lp: unknown; p1_change: unknown; p2_starting_lp: unknown; p2_change: unknown; language: unknown
}): { p1_remaining: number; p2_remaining: number; game_status: string } {
    const p1Remaining = (Number(params.p1_starting_lp) || 0) + (Number(params.p1_change) || 0)
    const p2Remaining = (Number(params.p2_starting_lp) || 0) + (Number(params.p2_change) || 0)
    const p1Defeated = p1Remaining <= 0
    const p2Defeated = p2Remaining <= 0
    let status: Record<string, string>
    if (p1Defeated && p2Defeated) status = BOTH_DEFEATED
    else if (p1Defeated) status = PLAYER1_DEFEATED
    else if (p2Defeated) status = PLAYER2_DEFEATED
    else status = GAME_CONTINUES
    return { p1_remaining: p1Remaining, p2_remaining: p2Remaining, game_status: t(status, params.language) }
}

// "MPG / Fuel Economy Calculator" - one shared formula, three unit systems
const UNIT_MPG: Record<string, string> = {
    en: 'MPG (US)', ru: 'миль/галлон (США)', de: 'MPG (US)', es: 'MPG (EE. UU.)', fr: 'MPG (US)', it: 'MPG (USA)', pl: 'MPG (USA)', lv: 'MPG (ASV)',
}
const UNIT_L_PER_100KM: Record<string, string> = {
    en: 'L/100km', ru: 'л/100км', de: 'L/100km', es: 'L/100km', fr: 'L/100km', it: 'L/100km', pl: 'L/100km', lv: 'L/100km',
}
const UNIT_KM_PER_L: Record<string, string> = {
    en: 'km/L', ru: 'км/л', de: 'km/L', es: 'km/L', fr: 'km/L', it: 'km/L', pl: 'km/L', lv: 'km/L',
}
export function fuelEconomyCalculator(params: { distance: unknown; fuel_used: unknown; mode: unknown; language: unknown }): {
    consumption: number; consumption_unit: string
} {
    const distance = Number(params.distance) || 0
    const fuelUsed = Number(params.fuel_used) || 1
    const mode = String(params.mode)
    if (mode === 'l_per_100km') {
        return { consumption: (fuelUsed / distance) * 100, consumption_unit: t(UNIT_L_PER_100KM, params.language) }
    }
    if (mode === 'km_per_liter') {
        return { consumption: distance / fuelUsed, consumption_unit: t(UNIT_KM_PER_L, params.language) }
    }
    return { consumption: distance / fuelUsed, consumption_unit: t(UNIT_MPG, params.language) }
}

// "What Is My IP Address" - pure passthrough of the server-detected visitor
// IP (injected into config.client_ip in page.tsx, forwarded into params by
// the json engine the same way config.language already is)
export function ipAddressDetector(params: { client_ip: unknown }): { ip_address: string } {
    return { ip_address: String(params.client_ip || 'unknown') }
}

// "Coin Flip Generator"
const HEADS_WORD: Record<string, string> = {
    en: 'Heads', ru: 'Орёл', de: 'Kopf', es: 'Cara', fr: 'Face', it: 'Testa', pl: 'Orzeł', lv: 'Ģerbonis',
}
const TAILS_WORD: Record<string, string> = {
    en: 'Tails', ru: 'Решка', de: 'Zahl', es: 'Cruz', fr: 'Pile', it: 'Croce', pl: 'Reszka', lv: 'Cipars',
}
export function coinFlipGenerator(params: { num_flips: unknown; language: unknown }): {
    results: string; heads_count: number; tails_count: number
} {
    const numFlips = Math.min(Math.max(Math.round(Number(params.num_flips)) || 1, 1), 1000)
    const heads = t(HEADS_WORD, params.language)
    const tails = t(TAILS_WORD, params.language)
    let headsCount = 0
    let tailsCount = 0
    const results: string[] = []
    for (let i = 0; i < numFlips; i++) {
        if (Math.random() < 0.5) {
            headsCount++
            results.push(heads)
        } else {
            tailsCount++
            results.push(tails)
        }
    }
    return { results: results.join(', '), heads_count: headsCount, tails_count: tailsCount }
}

// "Magic 8-Ball Decision Maker" - the question text isn't used in the logic
// (the answer is uniformly random, matching the physical toy), it's kept as
// an input purely for user engagement
const MAGIC_8_BALL_ANSWERS: Record<string, string>[] = [
    { en: 'It is certain.', ru: 'Это несомненно.', de: 'Es ist sicher.', es: 'Es cierto.', fr: 'C\'est certain.', it: 'È certo.', pl: 'To pewne.', lv: 'Tas ir noteikti.' },
    { en: 'It is decidedly so.', ru: 'Определённо да.', de: 'Es ist eindeutig so.', es: 'Decididamente así es.', fr: 'C\'est décidément ainsi.', it: 'È decisamente così.', pl: 'Zdecydowanie tak.', lv: 'Noteikti tā ir.' },
    { en: 'Without a doubt.', ru: 'Без сомнения.', de: 'Ohne Zweifel.', es: 'Sin duda.', fr: 'Sans aucun doute.', it: 'Senza dubbio.', pl: 'Bez wątpienia.', lv: 'Bez šaubām.' },
    { en: 'Yes, definitely.', ru: 'Да, определённо.', de: 'Ja, definitiv.', es: 'Sí, definitivamente.', fr: 'Oui, définitivement.', it: 'Sì, decisamente.', pl: 'Tak, zdecydowanie.', lv: 'Jā, noteikti.' },
    { en: 'You may rely on it.', ru: 'Можешь на это рассчитывать.', de: 'Darauf können Sie sich verlassen.', es: 'Puedes confiar en ello.', fr: 'Vous pouvez y compter.', it: 'Puoi contarci.', pl: 'Możesz na to liczyć.', lv: 'Uz to var paļauties.' },
    { en: 'As I see it, yes.', ru: 'Как я вижу, да.', de: 'So wie ich es sehe, ja.', es: 'Tal como yo lo veo, sí.', fr: 'À mon avis, oui.', it: 'Per come la vedo, sì.', pl: 'Z tego co widzę, tak.', lv: 'Kā es to redzu, jā.' },
    { en: 'Most likely.', ru: 'Скорее всего.', de: 'Höchstwahrscheinlich.', es: 'Lo más probable.', fr: 'Très probablement.', it: 'Molto probabile.', pl: 'Najprawdopodobniej.', lv: 'Visticamāk.' },
    { en: 'Outlook good.', ru: 'Перспективы хорошие.', de: 'Aussichten gut.', es: 'Buenas perspectivas.', fr: 'Perspective favorable.', it: 'Prospettive buone.', pl: 'Dobre perspektywy.', lv: 'Labas izredzes.' },
    { en: 'Yes.', ru: 'Да.', de: 'Ja.', es: 'Sí.', fr: 'Oui.', it: 'Sì.', pl: 'Tak.', lv: 'Jā.' },
    { en: 'Signs point to yes.', ru: 'Знаки указывают на да.', de: 'Zeichen deuten auf Ja hin.', es: 'Las señales apuntan a que sí.', fr: 'Les signes indiquent oui.', it: 'I segni indicano di sì.', pl: 'Znaki wskazują na tak.', lv: 'Zīmes norāda uz jā.' },
    { en: 'Reply hazy, try again.', ru: 'Ответ туманен, попробуй снова.', de: 'Antwort unklar, versuchen Sie es erneut.', es: 'Respuesta confusa, intenta de nuevo.', fr: 'Réponse floue, réessayez.', it: 'Risposta confusa, riprova.', pl: 'Odpowiedź niejasna, spróbuj ponownie.', lv: 'Atbilde neskaidra, mēģini vēlreiz.' },
    { en: 'Ask again later.', ru: 'Спроси позже.', de: 'Fragen Sie später noch einmal.', es: 'Pregunta más tarde.', fr: 'Redemandez plus tard.', it: 'Chiedi di nuovo più tardi.', pl: 'Zapytaj ponownie później.', lv: 'Pajautā vēlāk.' },
    { en: 'Better not tell you now.', ru: 'Лучше не говорить тебе сейчас.', de: 'Sage es dir jetzt lieber nicht.', es: 'Mejor no decírtelo ahora.', fr: 'Mieux vaut ne pas vous le dire maintenant.', it: 'Meglio non dirtelo ora.', pl: 'Lepiej nie mówić ci teraz.', lv: 'Labāk tagad nesaku.' },
    { en: 'Cannot predict now.', ru: 'Сейчас невозможно предсказать.', de: 'Kann jetzt nicht vorhersagen.', es: 'No se puede predecir ahora.', fr: 'Impossible à prédire maintenant.', it: 'Non posso prevedere ora.', pl: 'Nie można teraz przewidzieć.', lv: 'Tagad nevar paredzēt.' },
    { en: 'Concentrate and ask again.', ru: 'Сосредоточься и спроси снова.', de: 'Konzentrieren Sie sich und fragen Sie erneut.', es: 'Concéntrate y pregunta de nuevo.', fr: 'Concentrez-vous et redemandez.', it: 'Concentrati e chiedi di nuovo.', pl: 'Skoncentruj się i zapytaj ponownie.', lv: 'Koncentrējies un pajautā vēlreiz.' },
    { en: 'Don\'t count on it.', ru: 'Не рассчитывай на это.', de: 'Verlassen Sie sich nicht darauf.', es: 'No cuentes con ello.', fr: 'N\'y comptez pas.', it: 'Non contarci.', pl: 'Nie licz na to.', lv: 'Nerēķinies ar to.' },
    { en: 'My reply is no.', ru: 'Мой ответ — нет.', de: 'Meine Antwort ist nein.', es: 'Mi respuesta es no.', fr: 'Ma réponse est non.', it: 'La mia risposta è no.', pl: 'Moja odpowiedź brzmi nie.', lv: 'Mana atbilde ir nē.' },
    { en: 'My sources say no.', ru: 'Мои источники говорят нет.', de: 'Meine Quellen sagen nein.', es: 'Mis fuentes dicen que no.', fr: 'Mes sources disent non.', it: 'Le mie fonti dicono di no.', pl: 'Moje źródła mówią nie.', lv: 'Mani avoti saka nē.' },
    { en: 'Outlook not so good.', ru: 'Перспективы не очень хорошие.', de: 'Aussichten nicht so gut.', es: 'Perspectivas no muy buenas.', fr: 'Perspective peu favorable.', it: 'Prospettive non molto buone.', pl: 'Perspektywy niezbyt dobre.', lv: 'Izredzes nav tik labas.' },
    { en: 'Very doubtful.', ru: 'Весьма сомнительно.', de: 'Sehr zweifelhaft.', es: 'Muy dudoso.', fr: 'Très douteux.', it: 'Molto dubbio.', pl: 'Bardzo wątpliwe.', lv: 'Ļoti apšaubāmi.' },
]
export function magic8BallDecisionMaker(params: { question: unknown; language: unknown }): { answer: string } {
    const pick = MAGIC_8_BALL_ANSWERS[Math.floor(Math.random() * MAGIC_8_BALL_ANSWERS.length)]
    return { answer: t(pick, params.language) }
}

// "Random Playing Card Generator" - standard 52-card deck; numeric ranks
// (2-10) stay as digits across locales, only A/J/Q/K and the suit names
// need translation
const RANK_LABELS: Record<string, string>[] = [
    { en: 'Ace', ru: 'Туз', de: 'Ass', es: 'As', fr: 'As', it: 'Asso', pl: 'As', lv: 'Dūzis' },
    { en: '2', ru: '2', de: '2', es: '2', fr: '2', it: '2', pl: '2', lv: '2' },
    { en: '3', ru: '3', de: '3', es: '3', fr: '3', it: '3', pl: '3', lv: '3' },
    { en: '4', ru: '4', de: '4', es: '4', fr: '4', it: '4', pl: '4', lv: '4' },
    { en: '5', ru: '5', de: '5', es: '5', fr: '5', it: '5', pl: '5', lv: '5' },
    { en: '6', ru: '6', de: '6', es: '6', fr: '6', it: '6', pl: '6', lv: '6' },
    { en: '7', ru: '7', de: '7', es: '7', fr: '7', it: '7', pl: '7', lv: '7' },
    { en: '8', ru: '8', de: '8', es: '8', fr: '8', it: '8', pl: '8', lv: '8' },
    { en: '9', ru: '9', de: '9', es: '9', fr: '9', it: '9', pl: '9', lv: '9' },
    { en: '10', ru: '10', de: '10', es: '10', fr: '10', it: '10', pl: '10', lv: '10' },
    { en: 'Jack', ru: 'Валет', de: 'Bube', es: 'Jota', fr: 'Valet', it: 'Fante', pl: 'Walet', lv: 'Kalps' },
    { en: 'Queen', ru: 'Дама', de: 'Dame', es: 'Reina', fr: 'Dame', it: 'Regina', pl: 'Dama', lv: 'Dāma' },
    { en: 'King', ru: 'Король', de: 'König', es: 'Rey', fr: 'Roi', it: 'Re', pl: 'Król', lv: 'Karalis' },
]
const SUIT_LABELS: Record<string, string>[] = [
    { en: 'Spades', ru: 'Пики', de: 'Pik', es: 'Picas', fr: 'Piques', it: 'Picche', pl: 'Piki', lv: 'Pīķi' },
    { en: 'Hearts', ru: 'Червы', de: 'Herz', es: 'Corazones', fr: 'Cœurs', it: 'Cuori', pl: 'Kiery', lv: 'Sirdis' },
    { en: 'Diamonds', ru: 'Бубны', de: 'Karo', es: 'Diamantes', fr: 'Carreaux', it: 'Quadri', pl: 'Karo', lv: 'Ruķi' },
    { en: 'Clubs', ru: 'Трефы', de: 'Kreuz', es: 'Tréboles', fr: 'Trèfles', it: 'Fiori', pl: 'Trefl', lv: 'Krusti' },
]
const OF_WORD: Record<string, string> = {
    en: 'of', ru: 'из', de: 'von', es: 'de', fr: 'de', it: 'di', pl: 'z', lv: 'no',
}
export function randomCardGenerator(params: { num_cards: unknown; allow_duplicates: unknown; language: unknown }): {
    drawn_cards: string
} {
    const numCards = Math.min(Math.max(Math.round(Number(params.num_cards)) || 1, 1), 52)
    const allowDuplicates = String(params.allow_duplicates) === 'yes'
    const of = t(OF_WORD, params.language)
    const deck: string[] = []
    for (let s = 0; s < SUIT_LABELS.length; s++) {
        for (let r = 0; r < RANK_LABELS.length; r++) {
            deck.push(`${t(RANK_LABELS[r], params.language)} ${of} ${t(SUIT_LABELS[s], params.language)}`)
        }
    }
    const drawn: string[] = []
    if (allowDuplicates) {
        for (let i = 0; i < numCards; i++) drawn.push(deck[Math.floor(Math.random() * deck.length)])
    } else {
        const pool = deck.slice()
        for (let i = 0; i < numCards && pool.length > 0; i++) {
            const idx = Math.floor(Math.random() * pool.length)
            drawn.push(pool.splice(idx, 1)[0])
        }
    }
    return { drawn_cards: drawn.join(', ') }
}

// "Random Team Generator" - shuffles a newline/comma-separated name list,
// then deals names round-robin into N teams
const TEAM_WORD: Record<string, string> = {
    en: 'Team', ru: 'Команда', de: 'Team', es: 'Equipo', fr: 'Équipe', it: 'Squadra', pl: 'Drużyna', lv: 'Komanda',
}
export function randomTeamGenerator(params: { names_list: unknown; num_teams: unknown; language: unknown }): { teams: string } {
    const names = String(params.names_list || '')
        .split(/[\n,]/)
        .map((n) => n.trim())
        .filter((n) => n.length > 0)
    const numTeams = Math.min(Math.max(Math.round(Number(params.num_teams)) || 2, 2), Math.max(names.length, 2))
    const shuffled = names.slice()
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    const teams: string[][] = Array.from({ length: numTeams }, () => [])
    shuffled.forEach((name, i) => teams[i % numTeams].push(name))
    const team = t(TEAM_WORD, params.language)
    return { teams: teams.map((names, i) => `${team} ${i + 1}: ${names.join(', ')}`).join('\n') }
}
